import { AgentStatus } from '@21k/shared';

import { updateTicketAssignee } from '../api/client';
import type { AssignableTeamGroup, TicketDetail, UserOption } from '../api/types';
import { formatAgentStatusWarning, normalizeAgentStatus } from './agentStatus';

export function findAssignableMember(
  assignableTeams: AssignableTeamGroup[],
  userId: string,
) {
  for (const team of assignableTeams) {
    const member = team.members.find((m) => m.userId === userId);
    if (member) return member;
  }
  return null;
}

export function findAgentOption(agents: UserOption[], userId: string) {
  return agents.find((a) => a.id === userId);
}

export async function changeTicketAssigneeWithWarning({
  ticketId,
  assignedToId,
  previousAssignedToId,
  assignableTeams,
  agents,
  confirm,
}: {
  ticketId: string;
  assignedToId: string | null;
  previousAssignedToId: string | null;
  assignableTeams?: AssignableTeamGroup[];
  agents?: UserOption[];
  confirm: (message: string) => Promise<boolean>;
}): Promise<{ applied: boolean; ticket?: TicketDetail }> {
  if (assignedToId === previousAssignedToId) {
    return { applied: false };
  }

  if (assignedToId) {
    const member = assignableTeams ? findAssignableMember(assignableTeams, assignedToId) : null;
    const agent = agents ? findAgentOption(agents, assignedToId) : null;
    const status = normalizeAgentStatus(member?.agentStatus ?? agent?.agentStatus);
    const firstName = member?.firstName ?? agent?.firstName ?? 'Agent';
    const lastName = member?.lastName ?? agent?.lastName ?? '';

    if (status !== AgentStatus.LIVE) {
      const ok = await confirm(
        `${formatAgentStatusWarning(firstName, lastName, status)}\n\nProceed anyway?`,
      );
      if (!ok) return { applied: false };
    }
  }

  const result = await updateTicketAssignee(ticketId, assignedToId);

  if (result.warning) {
    const ok = await confirm(`${result.warning}\n\nProceed anyway?`);
    if (!ok) {
      const reverted = await updateTicketAssignee(ticketId, previousAssignedToId);
      return { applied: false, ticket: reverted.ticket };
    }
  }

  return { applied: true, ticket: result.ticket };
}
